import { z } from "zod";

import { quoteCommoditySchema, quoteResponseSchema } from "./quote-schema.ts";

export const contactSchema = z.object({
	name: z.string().nullable(),
	phone: z.string().nullable(),
	extension: z.string().nullable(),
	email: z.string().nullable(),
});

export const pickupDeliverySchema = z.object({
	id: z.number().nullable().optional(),
	shipmentHoursFrom: z.string().nullable(),
	shipmentHoursTo: z.string().nullable(),
	shipmentNumber: z.string().nullable(),
	companyName: z.string().nullable(),
	address: z.string().nullable().optional(),
	address1: z.string().nullable().optional(),
	address2: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	stateProvince: z.string().nullable().optional(),
	country: z.string().nullable().optional(),
	postalCode: z.string().nullable().optional(),
	locationType: z.string().nullable(),
	contact: contactSchema,
	scheduled: z.string().nullable(),
	actual: z.string().nullable(),
	specialInstructions: z.string().nullable(),
	appointmentType: z.string().nullable(),
	accessorials: z.array(z.string()).nullable(),
});

export const sourceSchema = z.object({
	origin: z.string().nullable(),
	name: z.string().nullable().optional(),
	email: z.string().nullable().optional(),
	// Legacy API field; remove once the API ships name/email (ADO 550805).
	user: z.string().nullable().optional(),
	date: z.string().nullable(),
});

export const additionalDetailsSchema = z.object({
	emergencyContactName: z.string().nullable(),
	emergencyContactNumber: z.string().nullable(),
	agentPro: z.string().nullable().optional(),
	customerReference: z.string().nullable(),
	soNumber: z.string().nullable(),
	poNumber: z.string().nullable(),
	loadValue: z.number().nullable(),
	rateType: z.string().nullable().optional(),
	quoteSource: sourceSchema.nullable(),
	tenderSource: sourceSchema.nullable(),
	customerPONumber: z.string().nullable(),
	sendToAccounting: z.boolean().optional(),
	// Bill of lading notations from the contract the load was rated from (Contract Management).
	bolNotations: z.string().nullable().optional(),
	contractId: z.number().nullable().optional(),
});

export const lineItemSchema = z.object({
	handlingUnit: z.string().nullable(),
	unit: z.string().nullable(),
	totalWeight: z.string().nullable(),
	length: z.string().nullable(),
	width: z.string().nullable(),
	height: z.string().nullable(),
	pieceCount: z.string().nullable(),
	stackable: z.boolean().nullable(),
	hazmat: z.boolean().nullable(),
	commodityDescription: z.string().nullable(),
	class: z.string().nullable(),
	nmfc: z.string().nullable(),
	density: z.number().nullable().optional(),
	estimatedClass: z.string().nullable().optional(),
	hazmatIdentificationNumber: z.string().nullable().optional(),
	hazmatProperShippingName: z.string().nullable().optional(),
	hazmatHazardClass: z.string().nullable().optional(),
	hazmatPackingGroup: z.string().nullable().optional(),
});

export const freightDetailsSchema = z.object({
	lineItems: z.array(lineItemSchema),
	totalWeight: z.string().nullable(),
	totalHandlingUnits: z.string().nullable(),
	calculatedLinearFeet: z.number().nullable(),
	overwriteCalculatedLinearFeet: z.number().nullable().optional(),
});

export const classCostSchema = z.object({
	type: z.string(),
	class: z.string(),
	description: z.string().nullable(),
	costPerPound: z.string().nullable(),
});

export const carrierServiceSchema = z.object({
	serviceLevel: z.string().nullable(),
	serviceType: z.string().nullable(),
	transitTime: z.number().nullable(),
	liabilityPerPound: z.string().nullable(),
	byClass: z.array(classCostSchema).nullable(),
	destinationTerminalCode: z.string().nullable(),
	originTerminalCode: z.string().nullable(),
	carrierId: z.number().nullable(),
	scac: z.string().nullable(),
});

export const chargeSchema = z.object({
	description: z.string().nullable(),
	amount: z.number(),
});

export const shipmentCustomerReferenceSchema = z.object({
	customerId: z.number().nullable(),
	customerName: z.string().nullable(),
});

export const customerSchema = z.object({
	id: z.number().optional(),
	customerName: z.string().nullable(),
	customerRate: z.string().nullable().default(null),
	isBillHoldCustomer: z.boolean().nullable(),
	billingCustomer: shipmentCustomerReferenceSchema.nullable().optional(),
	tenderedCustomer: shipmentCustomerReferenceSchema.nullable().optional(),
	billedAmount: z.number().nullable().optional(),
	charges: z.array(chargeSchema).nullable().optional(),
	totalCharges: z.number().nullable().optional(),
	primaryBroker: contactSchema.nullable(),
	ltlBroker: contactSchema.nullable(),
});

export const carrierSchema = z.object({
	id: z.number().nullable().optional(),
	carrierId: z.number().nullable().optional(),
	carrierName: z.string().nullable(),
	pro: z.string().nullable(),
	agentPro: z.string().nullable().optional(),
	carrierQuoteId: z.string().nullable(),
	expDate: z.string().nullable(),
	contactInfo: contactSchema,
	charges: z.array(chargeSchema).nullable(),
	carrierWebsite: z.string().nullable().optional(),
	confirmationNumber: z.string().optional().nullable(),
	carrierRate: z.string().nullable().default(null),
});

export const redFlagSchema = z.object({
	flagType: z.number(),
	name: z.string(),
	description: z.string(),
	severity: z.string(),
});

export const shipmentExceptionSchema = z.object({
	id: z.number().nullable().optional(),
	isCustomer: z.boolean(),
	message: z.string(),
	isImmediateAction: z.boolean().nullable(),
	isNeedsAcknowledged: z.boolean().nullable(),
});

export const remarkSchema = z.object({
	dateEntered: z.string(),
	text: z.string(),
	contactInfo: contactSchema,
});

export const checkCallSchema = z.object({
	id: z.string().optional(),
	// API only populates this for Dispatched, Loaded, and Delivered events.
	status: z.string().nullable(),
	timestamp: z.string(),
	city: z.string().nullable(),
	state: z.string().nullable(),
	zip: z.string().nullable(),
	enteredBy: z.string().nullable(),
	country: z.string().nullable().optional(),
	remark: z.string().nullable(),
});

export const remarkDetailsSchema = z.object({
	id: z.number().optional(),
	dateEntered: z.string(),
	text: z.string(),
	contactInfo: contactSchema,
});

export const newRemarkResponseSchema = z.object({
	poNumber: z.number(),
	remarkText: z.string(),
	isUrgent: z.boolean(),
	remarkId: z.number(),
	createdByUserId: z.number(),
	dateCreated: z.string(),
	dateUpdated: z.string(),
	updatedByUserId: z.number(),
});

export const createFlagResponseSchema = z.object({
	exceptionId: z.number(),
	exceptionType: z.number(),
	customerId: z.number(),
	shipmentId: z.number(),
	quoteId: z.number().nullable(),
	message: z.string(),
	createdDate: z.string(),
	effectiveDate: z.string(),
	isResolved: z.boolean(),
	resolvedDate: z.string().nullable(),
	exceptionSource: z.string(),
	isAcknowledged: z.boolean(),
	acknowledgedDate: z.string().nullable(),
	acknowledgedByEmployeeId: z.number().nullable(),
	isImmediateAction: z.boolean().nullable(),
	isNeedsAcknowledged: z.boolean().nullable(),
});

export const loadComplianceDetailSchema = z.object({
	loadComplianceStatusID: z.number(),
	isEDICustomer: z.boolean(),
});
export type LoadComplianceDetail = z.infer<typeof loadComplianceDetailSchema>;

export const loadAccessSchema = z.object({
	permissions: z.object({
		canViewCustomer: z.boolean(),
		canViewFinancials: z.boolean(),
	}),
	isReadOnly: z.boolean(),
	statuses: z.array(z.string()),
	lock: z
		.object({
			lockedByUserId: z.number(),
			lockedAt: z.string(),
			lockedByUserName: z.string().nullable().optional(),
			lockedByUserEmail: z.string().nullable().optional(),
		})
		.nullable(),
});
export type LoadAccess = z.infer<typeof loadAccessSchema>;

export const quoteRecallAccessorialSchema = z.object({
	id: z.number(),
	name: z.string().nullable(),
	stopTypeId: z.number().nullable(),
	accessorialUsageTypeId: z.number().nullable(),
});

export const quoteRecallLocationSchema = z.object({
	name: z.string().nullable(),
	address1: z.string().nullable(),
	address2: z.string().nullable(),
	postalCode: z.string().nullable(),
	city: z.string().nullable(),
	state: z.string().nullable(),
	country: z.string().nullable(),
});

export const quoteRecallStopDetailsSchema = z.object({
	stopName: z.string().nullable(),
	contactName: z.string().nullable(),
	contactPhone: z.string().nullable(),
	contactExtension: z.string().nullable(),
	address1: z.string().nullable(),
	address2: z.string().nullable(),
	hoursOpen: z.string().nullable(),
	hoursClose: z.string().nullable(),
});

export const quoteRecallSchema = z.object({
	quoteCommodities: z.array(quoteCommoditySchema),
	accessorials: z.array(quoteRecallAccessorialSchema),
	origin: quoteRecallLocationSchema,
	destination: quoteRecallLocationSchema,
	pickupDetails: quoteRecallStopDetailsSchema,
	deliveryDetails: quoteRecallStopDetailsSchema,
	shipmentDate: z.string().nullable(),
	billingCustomerId: z.number().nullable(),
	calculatedLinearFeet: z.number().nullable(),
	carrierScac: z.string().nullable(),
	customerId: z.number().nullable(),
	email: z.string().nullable(),
	publicGuid: z.string().nullable(),
	isVolume: z.boolean().nullable(),
	isLtlTender: z.boolean().nullable(),
	specialInstructions: z.string().nullable(),
	totalLinearFeet: z.number().nullable(),
	pieceCaseCount: z.number().nullable(),
	sourceId: z.number().nullable(),
	templateId: z.number().nullable(),
});

export const shipmentManagementSchema = z.object({
	quoteId: z.number().nullable(),
	shipmentDetails: z.object({
		pickup: pickupDeliverySchema,
		delivery: pickupDeliverySchema,
		additionalDetails: additionalDetailsSchema,
		freightDetails: freightDetailsSchema,
		needsRerate: z.boolean().optional().nullable(),
	}),
	carrierService: carrierServiceSchema,
	customer: customerSchema,
	carrier: carrierSchema,
	insurance: z.object({
		totalAmount: z.number().nullable(),
		isInsured: z.boolean().nullable().default(false),
	}),
	shipmentProgress: z.object({
		shipmentState: z.union([
			z.literal(0),
			z.literal(1),
			z.literal(2),
			z.literal(3),
		]),
	}),
	loadComplianceDetail: loadComplianceDetailSchema.nullable(),
	loadAccess: loadAccessSchema.optional().nullable(),
	trackingHistory: z.object({
		checkCalls: z.array(checkCallSchema),
	}),
	payVendor: z
		.object({
			totalAmount: z.number().nullable(),
		})
		.optional()
		.nullable(),
	tqlSalesContacts: z.object({
		lae: contactSchema,
		ltlar: contactSchema,
	}),
	accessorials: z.array(z.string()).nullable(),
	remarks: z.array(remarkDetailsSchema).optional().nullable(),
	exceptions: z.array(shipmentExceptionSchema).optional().nullable(),
	isMLS: z.boolean().nullable(),
	quoteRecall: quoteRecallSchema.optional().nullable(),
});

export const newClaimRequestSchema = z.object({
	carrierId: z.number(),
	claimReasonId: z.number(),
	overFreight: z.boolean(),
	estimatedValue: z.number(),
	actualValue: z.number(),
	salvageAmount: z.number(),
	federalInspection: z.boolean().nullable(),
	tempRecording: z.boolean().nullable(),
	salvageHelp: z.boolean(),
	customerContactName: z.string(),
	customerContactEmail: z.string(),
	customerContactPhone: z.string(),
	customerContactPhoneExt: z.string(),
	claimDescription: z.string(),
});

export const claimReasonSchema = z.object({
	id: z.union([z.string(), z.number()]),
	name: z.string(),
});

export const shipmentClaimResponseSchema = z.object({
	poNumber: z.number(),
});

export const dispatchResponseSchema = z.object({
	success: z.boolean(),
	validationErrors: z.array(z.string()).nullish(),
	poNumber: z.string().nullish(),
	errorMessage: z.string().nullish(),
});

export const markLoadedStopSchema = z.object({
	id: z.number(),
	carrierId: z.number().nullable(),
	shedName: z.string().nullable(),
	referenceNumber: z.string().nullable(),
	remarks: z.string().nullable(),
	isCompleted: z.boolean(),
	completedTime: z.string().nullable(),
	appointment: z.object({
		date: z.string().nullable(),
		startTime: z.string().nullable(),
		endTime: z.string().nullable(),
		type: z.number().nullable(),
		notes: z.string().nullable(),
	}),
	location: z.object({
		address1: z.string().nullable(),
		address2: z.string().nullable(),
		city: z.string().nullable(),
		state: z.string().nullable(),
		zip: z.string().nullable(),
		country: z.string().nullable(),
	}),
	contact: z.object({
		name: z.string().nullable(),
		phone: z.string().nullable(),
		extension: z.string().nullable(),
		hoursOfOperation: z.string().nullable(),
	}),
});

export const voidShipmentResponseSchema = z.object({
	success: z.boolean().nullish(),
	errors: z.array(z.string()).nullish(),
	carrierDispatchCancelled: z.boolean().nullish(),
	confirmationNumber: z.string().nullish(),
	carrierRejectionMessage: z.string().nullish(),
	shipmentStateAtVoid: z.number().nullish(),
});

export const markLoadedResponseSchema = z.object({
	pickups: z.array(markLoadedStopSchema),
});

export const ShipmentUpdateResponseSchema = z.object({
	rerateQuote: quoteResponseSchema.nullable(),
	shipment: shipmentManagementSchema,
});

// mark-shipment-unloaded returns a bare array of stops (unlike mark-loaded,
// which wraps them in a `pickups` object).
export const markUnloadedResponseSchema = z.array(markLoadedStopSchema);

export type ShipmentManagement = z.infer<typeof shipmentManagementSchema>;
export type ShipmentDetailsData = z.infer<
	typeof shipmentManagementSchema.shape.shipmentDetails
>;
export type ShipmentManagementPickupDeliveryDetails = z.infer<
	typeof pickupDeliverySchema
>;
export type ShipmentFreightDetails = z.infer<typeof freightDetailsSchema>;
export type ShipmentLineItem = z.infer<typeof lineItemSchema>;
export type ShipmentAdditionalDetails = z.infer<typeof additionalDetailsSchema>;
export type ShipmentCarrierService = z.infer<typeof carrierServiceSchema>;
export type ShipmentCustomer = z.infer<typeof customerSchema>;
export type ShipmentCarrier = z.infer<typeof carrierSchema>;
export type ShipmentLineItemCost = z.infer<typeof chargeSchema>;
export type ShipmentInsuranceOrPayVendor = z.infer<
	typeof shipmentManagementSchema.shape.payVendor
>;

export type ShipmentSalesContacts = z.infer<
	typeof shipmentManagementSchema.shape.tqlSalesContacts
>;
export type ShipmentException = z.infer<typeof shipmentExceptionSchema>;
export type QuoteRecall = z.infer<typeof quoteRecallSchema>;
export type ShipmentRemark = z.infer<typeof remarkDetailsSchema>;
export type NewRemarkResponse = z.infer<typeof newRemarkResponseSchema>;
export type CreateFlagResponse = z.infer<typeof createFlagResponseSchema>;
export type DispatchResponse = z.infer<typeof dispatchResponseSchema>;
export type ShipmentCheckCall = z.infer<typeof checkCallSchema>;

export const shipmentStatusEnumsSchema = z.record(z.string(), z.string());
export type ShipmentStatusEnums = z.infer<typeof shipmentStatusEnumsSchema>;

export type ShipmentManagementProgressDetails = z.infer<
	typeof shipmentManagementSchema.shape.shipmentProgress
>;

export type ShipmentClaimResponse = z.infer<typeof shipmentClaimResponseSchema>;
export type NewClaimRequest = z.infer<typeof newClaimRequestSchema>;
export type ShipmentClaimReason = z.infer<typeof claimReasonSchema>;
export type MarkLoadedStop = z.infer<typeof markLoadedStopSchema>;
export type MarkLoadedResponse = z.infer<typeof markLoadedResponseSchema>;
export type VoidShipmentResponse = z.infer<typeof voidShipmentResponseSchema>;
